import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowUpRight, Check, Loader2, Lock, Sparkles } from "lucide-react";

import { API } from "./constants";
import { CASH_IMPACT_LABEL, OPPS_COUNT } from "./aigoMetrics";

const STORAGE_KEY = "aigo_upsell_dismissed_v1";

/**
 * UpsellModal — captures email + optional "which fix would you unlock first?"
 * intent when a Free user hits a locked Pro feature. Stripe is removed; this
 * is a real waitlist that persists server-side (POST /api/waitlist).
 *
 * Shown ONCE per browser session — dismissal is persisted to localStorage
 * so we don't badger returning visitors. Reset by clearing the key.
 *
 * data-testids: upsell-modal, upsell-modal-close, upsell-modal-email,
 * upsell-modal-intent, upsell-modal-submit, upsell-modal-success,
 * upsell-modal-error, upsell-modal-backdrop.
 */
export const UpsellModal = ({ source, plan = "pro", onClose }) => {
  const [email, setEmail] = useState("");
  const [intent, setIntent] = useState("");
  const [state, setState] = useState({ loading: false, ok: null, msg: "" });

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState({ loading: true, ok: null, msg: "" });
    try {
      await axios.post(`${API}/waitlist`, {
        email, intent: intent || undefined, plan, source,
      });
      setState({
        loading: false,
        ok: true,
        msg: "You’re in. We’ll email you the moment Pro opens — likely within a week or two.",
      });
      try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* ignore */ }
      if (window.gtag) window.gtag("event", "waitlist_join", { source, plan });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setState({
        loading: false,
        ok: false,
        msg: typeof detail === "string" ? detail : "Couldn’t submit — try again in a moment.",
      });
    }
  };

  return (
    <div
      data-testid="upsell-modal-backdrop"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      style={{ animation: "fadein 200ms ease-out both" }}
      onClick={onClose}
    >
      <div
        data-testid="upsell-modal"
        className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-emerald-500/20 overflow-hidden"
        style={{ animation: "lift 320ms cubic-bezier(.2,.8,.2,1) both" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                <Lock className="w-3 h-3" /> Pro · early access
              </div>
              <h3 className="mt-1 text-lg sm:text-xl font-bold text-slate-900 leading-tight">
                Unlock 1-click Implement.<br className="hidden sm:block" />
                Join the Pro waitlist.
              </h3>
            </div>
            <button
              data-testid="upsell-modal-close"
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 w-8 h-8 rounded-full hover:bg-slate-100 text-slate-500 text-lg leading-none"
            >
              ×
            </button>
          </div>

          <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">
            Diagnosis is free — every leak and every $/mo impact is visible now.
            <b className="text-slate-900"> Execution</b> (1-click Implement, Approve-all-safe · {CASH_IMPACT_LABEL}, Autonomous Mode) opens to waitlist members first.
          </p>

          {state.ok ? (
            <div
              data-testid="upsell-modal-success"
              className="mt-5 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold flex items-start gap-2"
            >
              <Check className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{state.msg}</span>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-3">
              <input
                data-testid="upsell-modal-email"
                type="email"
                required
                placeholder="ops@yourbrand.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
              <input
                data-testid="upsell-modal-intent"
                type="text"
                placeholder={`Which fix would you unlock first? (optional · ${OPPS_COUNT} opps queued)`}
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                maxLength={140}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
              <button
                data-testid="upsell-modal-submit"
                type="submit"
                disabled={state.loading}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white text-sm font-bold transition shadow-sm shadow-emerald-500/30"
              >
                {state.loading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <><Sparkles className="w-4 h-4" /> Join Pro waitlist <ArrowUpRight className="w-4 h-4" /></>}
              </button>
              {state.msg && state.ok === false && (
                <div data-testid="upsell-modal-error" className="text-xs text-rose-600 font-semibold">
                  {state.msg}
                </div>
              )}
              <p className="text-[10px] text-slate-400 text-center leading-relaxed pt-1">
                We’ll email you once. No spam. One-tap unsubscribe.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * useUpsellTrigger — provides an `open(source)` function that shows the
 * upsell modal. If the user has previously dismissed the modal or already
 * submitted the waitlist form (both persist `STORAGE_KEY` to localStorage),
 * subsequent `open(source)` calls become no-ops. Clears with
 * `localStorage.removeItem("aigo_upsell_dismissed_v1")` for testing.
 *
 * Returns:  { modal, open }
 *   modal — the JSX to render at the end of the tree
 *   open  — function(source) to invoke on Pro-lock click
 */
export const useUpsellTrigger = () => {
  const [openState, setOpenState] = useState(null);

  const open = (source) => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return; // already dismissed / joined
    } catch (_) { /* ignore private-mode denials */ }
    setOpenState({ source: source || "unknown" });
  };

  const handleClose = () => {
    setOpenState(null);
    // Remember dismissal so we don't re-badger the user this session
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* ignore */ }
  };

  const modal = openState
    ? <UpsellModal source={openState.source} onClose={handleClose} />
    : null;

  return { modal, open };
};

export default UpsellModal;
